import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { format, parseISO } from 'date-fns';
import { useBoardChartData } from '../../hooks/useBoardChartData';
import { BaseChartProps } from './types';
import { CHART_COLORS, CHART_STYLES, getCurrencySymbol, formatCompactNumber, formatCurrency } from './chartTheme';
import { useSetChartMetadata } from './ChartContext';
import { resolveEventDisplayName } from '../../../../lib/constants/channelEvents';
import {
    ResponsiveContainer,
    ComposedChart,
    Area,
    Scatter,
    XAxis,
    YAxis,
    CartesianGrid,
    Tooltip,
} from 'recharts';

interface DayData {
    date: string;
    revenue: number;
    eventCount: number;
    eventNames: string[];
}

export const RevenueWithActionMarkersChart: React.FC<BaseChartProps> = () => {
    const { t } = useTranslation();
    const { finance, events, currencySettings } = useBoardChartData();

    const currencySymbol = useMemo(
        () => getCurrencySymbol(currencySettings?.default),
        [currencySettings?.default]
    );

    const chartData = useMemo(() => {
        if (!finance?.dailyBreakdown) return [];

        const eventsByDate: Record<string, string[]> = {};
        if (events && events.length > 0) {
            for (const event of events) {
                if (!event.date) continue;
                const dateKey = event.date.substring(0, 10);
                if (!eventsByDate[dateKey]) eventsByDate[dateKey] = [];
                eventsByDate[dateKey].push(resolveEventDisplayName(event, t));
            }
        }

        return Object.entries(finance.dailyBreakdown)
            .sort(([a], [b]) => a.localeCompare(b))
            .map(([date, day]: [string, Record<string, unknown>]) => {
                const metrics = day.metrics as Record<string, number> | undefined;
                const revenue = metrics?.revenue ?? 0;
                const dayEvents = eventsByDate[date] || [];
                return {
                    date,
                    revenue,
                    eventCount: dayEvents.length > 0 ? dayEvents.length : undefined,
                    eventNames: dayEvents,
                } as DayData;
            });
    }, [finance?.dailyBreakdown, events, t]);

    const isEmpty = useMemo(() => chartData.length === 0, [chartData]);

    useSetChartMetadata({ isEmpty });

    if (isEmpty) return null;

    const CustomTooltip = ({ active, payload, label }: Record<string, unknown>) => {
        if (!active || !payload || !(payload as Array<Record<string, unknown>>).length) return null;
        const data = (payload as Array<Record<string, unknown>>)[0]?.payload as DayData | undefined;
        if (!data) return null;

        return (
            <div style={CHART_STYLES.tooltip.contentStyle}>
                <p className="text-[10px] font-semibold mb-1">
                    {format(parseISO(label as string), 'MMM d, yyyy')}
                </p>
                <p className="text-[10px]">
                    {t('charts.revenueWithMarkers.revenue', 'Revenue')}: {formatCurrency(data.revenue, currencySymbol)}
                </p>
                {data.eventNames && data.eventNames.length > 0 && (
                    <div className="mt-1 pt-1 border-t border-subtle">
                        <p className="text-[10px] text-primary/60 mb-0.5">
                            {t('charts.revenueWithMarkers.events', 'Events')} ({data.eventNames.length}):
                        </p>
                        {data.eventNames.slice(0, 5).map((name, i) => (
                            <p key={i} className="text-[10px] text-primary/80 truncate">
                                {name}
                            </p>
                        ))}
                        {data.eventNames.length > 5 && (
                            <p className="text-[10px] text-primary/50">
                                +{data.eventNames.length - 5} {t('charts.revenueWithMarkers.more', 'more')}
                            </p>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div className="w-full h-full bg-transparent overflow-hidden flex flex-col">
            <div className="flex-1 min-h-0 p-2">
                <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={chartData} margin={CHART_STYLES.margin.withAxis}>
                        <CartesianGrid
                            vertical={false}
                            stroke={CHART_STYLES.grid.stroke}
                            strokeDasharray={CHART_STYLES.grid.strokeDasharray}
                            strokeWidth={CHART_STYLES.grid.strokeWidth}
                            opacity={CHART_STYLES.grid.opacity}
                        />
                        <XAxis
                            dataKey="date"
                            tickFormatter={(v: string) => format(parseISO(v), 'MMM d')}
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            yAxisId="revenue"
                            tickFormatter={(v: number) => formatCompactNumber(v, currencySymbol)}
                            {...CHART_STYLES.axis}
                        />
                        <YAxis
                            yAxisId="events"
                            orientation="right"
                            tickFormatter={(v: number) => String(Math.round(v))}
                            {...CHART_STYLES.axis}
                            hide
                        />
                        <Tooltip content={<CustomTooltip />} />
                        <Area
                            yAxisId="revenue"
                            type="monotone"
                            dataKey="revenue"
                            stroke={CHART_COLORS.revenue}
                            fill={CHART_COLORS.revenue}
                            fillOpacity={0.1}
                            strokeWidth={2}
                            name={t('charts.revenueWithMarkers.revenue', 'Revenue')}
                        />
                        <Scatter
                            yAxisId="events"
                            dataKey="eventCount"
                            fill={CHART_COLORS.chart.line5}
                            name={t('charts.revenueWithMarkers.events', 'Events')}
                        />
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    );
};
